import { Clear, Search } from '@mui/icons-material'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  Typography,
  IconButton,
  TablePagination,
  Tooltip,
  Grow,
  InputAdornment,
  TextField,
  TableFooter,
  Stack,
  Button,
} from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { Leader, User } from '../../store/types'
import { useTranslation } from 'react-i18next'
import { visuallyHidden } from '@mui/utils'
import { StyledContainerRowBetween } from '../../styles/ContainerStyles'
import { defaultPageSize, defaultRowsPerPageOptions, smallDefaultPageSize } from '../../config/defaultPagination'
import { Order } from '../../store/types/components/table/order'
import { StyledTableSortLabel } from '../../styles/TableStyles'
import { StyledFooterCell, StyledHeaderCell } from '../../styles/WorklogTableStyles'
import AvatarComponent from '../avatar/AvatarComponent'
import SelectEmployee from '../Form/SelectEmployee'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import { getComparator, stableSort } from '../../store/utils/sortingFunctions'

type TeamTableProps = {
  editable: boolean | undefined
  replace: (employees: User[]) => void
  watchedUsers: User[] | undefined
  users: User[] | undefined
  smallDefaultTable?: boolean
  leaders: Leader[] | undefined
}

const TeamTable = ({ editable, replace, watchedUsers, users, smallDefaultTable, leaders }: TeamTableProps) => {
  const [employees, setEmployees] = useState<User[]>([])
  useMemo(() => {
    if (watchedUsers !== undefined) {
      setEmployees(watchedUsers)
      return
    }

    if (users !== undefined) {
      setEmployees(users)
      return
    }

    setEmployees([])
  }, [users, watchedUsers])

  const { t } = useTranslation()

  const [selected, setSelected] = useState<User[]>([])

  const handleClick = (event: React.MouseEvent<unknown>, employee: User) => {
    const selectedIndex = selected.findIndex(entry => entry.id === employee.id)
    let newSelected: User[] = [...selected]

    if (selectedIndex === -1) {
      newSelected.push(employee)
    } else {
      newSelected.splice(selectedIndex, 1)
    }

    setSelected(newSelected)
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(employees)
      return
    }
    setSelected([])
  }

  const isSelected = (employee: User) => {
    let indexes = selected.map(entry => entry.id)
    return indexes.indexOf(employee.id) !== -1
  }

  const handleDeleteSelected = () => {
    let selectedIndexes = selected.map(entry => entry.id)
    replace(employees.filter(employee => !selectedIndexes.includes(employee.id)))
    setSelected([])
  }

  const deleteVisible = useMemo(() => {
    return selected.length !== 0
  }, [selected])

  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<string>('firstName+lastName')

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property)
  }

  const [input, setInput] = useState('')

  let rowCount = employees.length
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem(LocalStorageTypes.TeamTableRows)
      ? parseInt(localStorage.getItem(LocalStorageTypes.TeamTableRows)!)
      : !smallDefaultTable
      ? defaultPageSize
      : smallDefaultPageSize
  )

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    localStorage.setItem(LocalStorageTypes.TeamTableRows, String(parseInt(event.target.value, 10)))
    setPage(0)
  }

  const filteredEmployees = useMemo(() => {
    if (input === '') return employees

    return employees.filter(
      employee => employee.firstName.toLowerCase().includes(input) || employee.lastName.toLowerCase().includes(input)
    )
  }, [input, employees])

  const visibleRows = useMemo(
    () =>
      stableSort(
        employees.filter(
          employee =>
            employee.firstName.toLowerCase().includes(input.toLowerCase()) ||
            employee.lastName.toLowerCase().includes(input.toLowerCase())
        ),
        getComparator(order, orderBy as keyof User)
      ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, page, rowsPerPage, employees, input, orderBy]
  )

  useEffect(() => {
    setInput('')
  }, [editable])

  const pageSize = Math.min(rowsPerPage, defaultPageSize)
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * pageSize - employees.length) : pageSize - visibleRows.length

  useEffect(() => {
    if (emptyRows === rowsPerPage && page !== 0) {
      setPage(page - 1)
    }
  }, [emptyRows, page, rowsPerPage, employees])

  return (
    <Stack direction="column" sx={{ gap: 2 }}>
      {editable ? (
        <StyledContainerRowBetween>
          <SelectEmployee employees={employees} replace={replace} leaders={leaders} />

          <Grow in={deleteVisible}>
            <Button
              size="medium"
              variant="contained"
              color="secondary"
              onClick={() => handleDeleteSelected()}
              sx={{
                display: deleteVisible ? '' : 'none',
              }}
            >
              <Clear />
              {t('page.usersProfile.delete')}
            </Button>
          </Grow>
        </StyledContainerRowBetween>
      ) : (
        <TextField
          placeholder={t('components.placeholder.search')}
          size="small"
          fullWidth={true}
          value={input}
          onChange={e => {
            setInput(e.target.value)
            setPage(0)
          }}
          sx={{
            svg: {
              color: 'text.base',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            inputProps: {
              'aria-labelledby': 'team-header',
            },
          }}
        />
      )}

      <TableContainer sx={{ border: '1px solid', borderColor: 'background.elevation8' }}>
        <Table sx={{ minWidth: 150 }} size="small">
          <TableHead>
            <TableRow>
              {editable && (
                <StyledHeaderCell
                  id={'header-checkbox-all'}
                  aria-label={t('aria.myProfile.checkboxSelectAll')}
                  padding="checkbox"
                >
                  <Checkbox
                    checked={rowCount > 0 && selected.length === rowCount}
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-labelledby': 'header-checkbox-all' }}
                  />
                </StyledHeaderCell>
              )}

              <StyledHeaderCell
                sx={{ px: editable ? 1 : 3, py: 1.5 }}
                sortDirection={orderBy === 'firstName+lastName' ? order : false}
              >
                <StyledTableSortLabel
                  sx={{
                    color: 'text.base',
                    '&.Mui-active': {
                      color: 'text.highEmphasis',
                    },
                  }}
                  active={orderBy === 'firstName+lastName'}
                  direction={orderBy === 'firstName+lastName' ? order : 'asc'}
                  onClick={createSortHandler('firstName+lastName')}
                >
                  {t('components.table.header.employee')}
                  {orderBy === 'firstName+lastName' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </StyledHeaderCell>

              {editable && (
                <StyledHeaderCell sx={{ width: '10%' }} align="center">
                  {t('components.table.header.action')}
                </StyledHeaderCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {employees.length === 0 || visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ height: 50 * Math.min(rowsPerPage, defaultPageSize) }} align="center">
                  <Typography variant="body1" fontWeight={500}>
                    {t('components.table.cell.noEmployees.title')}
                  </Typography>
                  {editable && (
                    <Typography variant="body2">{t('components.table.cell.noEmployees.subtitle')}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map(employee => {
                const isItemSelected = isSelected(employee)
                return (
                  <TableRow
                    sx={{ height: 50, borderBottom: '1px solid', borderColor: 'background.elevation9' }}
                    key={`table-row-${employee.id}`}
                  >
                    {editable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={event => handleClick(event, employee)}
                          inputProps={{
                            'aria-label': `checkbox-for-${employee.firstName}-${employee.lastName}`,
                          }}
                        />
                      </TableCell>
                    )}

                    <TableCell sx={{ display: 'flex', alignItems: 'center', px: editable ? 1 : 3, gap: 2, border: 0 }}>
                      <AvatarComponent
                        extension={employee.extension}
                        avatar={employee.avatar}
                        alt={`${employee.firstName}-${employee.lastName}-profile-picture`}
                        size={32}
                      />
                      <Box>
                        <Typography component={'h4'} variant="subtitle2">
                          {employee.firstName + ' ' + employee.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {employee.email}
                        </Typography>
                      </Box>
                    </TableCell>

                    {editable && (
                      <TableCell align="center">
                        <Tooltip title={t('components.tooltip.delete')} placement="right-end">
                          <IconButton
                            aria-label={`remove-${employee.firstName}-${employee.lastName}-from-team-table-button`}
                            size="small"
                            onClick={() => {
                              replace(employees.filter(entry => entry.id !== employee.id))
                            }}
                          >
                            <Clear sx={{ height: 16, width: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
            {emptyRows > 0 && visibleRows.length > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={4} />
              </TableRow>
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <StyledFooterCell colSpan={10} sx={{ height: 67 }}>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={defaultRowsPerPageOptions}
                  count={filteredEmployees.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  labelRowsPerPage={t('components.table.pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) => {
                    return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    border: 'none',
                    color: 'text.highEmphasis',
                    float: 'right',
                  }}
                  slotProps={{
                    select: {
                      inputProps: {
                        'aria-label': t('components.table.pagination.rowsPerPage'),
                        'aria-labelledby': 'rowsPage',
                      },
                      id: 'rowsPage',
                    },
                  }}
                />
              </StyledFooterCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default TeamTable
